/**
 * GoCharting indicator set: every GoCharting indicator that can be computed from OHLCV
 * candles and that the chart did not already have. (Order-flow tools such as footprint,
 * market profile, open interest and buy/sell VWAP need trade-level data and are not here.)
 *
 * Each entry: { fullLabel, shortLabel, group, overlay, paramDefs, compute(candles, p) }.
 * compute returns { lines, levels?, markers? }:
 *   lines:   [{ name, values (array aligned with candles; NaN = no value), kind?: "line" |
 *              "histogram", color?, lineStyle?, lineWidth?, colorBy?: "sign",
 *              priceScaleId?, scaleMargins? }]
 *   levels:  horizontal reference values drawn in the indicator's pane
 *   markers: lightweight-charts series markers for the price chart
 * Chart.js draws overlays on the price pane and everything else in its own pane.
 */

const N = NaN;
const ok = Number.isFinite;

// ---------- array helpers (all return arrays aligned with the input) ----------
const vol = (c) => (ok(c?.volume) && c.volume > 0 ? c.volume : 1);
const pluck = (candles, f) => candles.map(f);
const closes = (c) => pluck(c, (x) => x.close);
const highs = (c) => pluck(c, (x) => x.high);
const lows = (c) => pluck(c, (x) => x.low);
const vols = (c) => pluck(c, vol);
const hl2 = (c) => pluck(c, (x) => (x.high + x.low) / 2);
const hlc3 = (c) => pluck(c, (x) => (x.high + x.low + x.close) / 3);

export function sma(src, n) {
  const out = new Array(src.length).fill(N);
  let sum = 0;
  let count = 0;
  const q = [];
  for (let i = 0; i < src.length; i += 1) {
    const v = src[i];
    q.push(v);
    if (ok(v)) {
      sum += v;
      count += 1;
    }
    if (q.length > n) {
      const old = q.shift();
      if (ok(old)) {
        sum -= old;
        count -= 1;
      }
    }
    if (q.length === n && count === n) out[i] = sum / n;
  }
  return out;
}

/** EMA seeded with the SMA of the first n valid values; alpha defaults to 2/(n+1). */
export function ema(src, n, alpha = 2 / (n + 1)) {
  const out = new Array(src.length).fill(N);
  let prev = N;
  let seedSum = 0;
  let seedCount = 0;
  for (let i = 0; i < src.length; i += 1) {
    const v = src[i];
    if (!ok(v)) continue;
    if (!ok(prev)) {
      seedSum += v;
      seedCount += 1;
      if (seedCount === n) {
        prev = seedSum / n;
        out[i] = prev;
      }
      continue;
    }
    prev = alpha * v + (1 - alpha) * prev;
    out[i] = prev;
  }
  return out;
}

/** Wilder / smoothed moving average (RMA, SMMA). */
export const rma = (src, n) => ema(src, n, 1 / n);

export function wma(src, n) {
  const out = new Array(src.length).fill(N);
  const denom = (n * (n + 1)) / 2;
  for (let i = n - 1; i < src.length; i += 1) {
    let s = 0;
    let good = true;
    for (let k = 0; k < n; k += 1) {
      const v = src[i - k];
      if (!ok(v)) {
        good = false;
        break;
      }
      s += v * (n - k);
    }
    if (good) out[i] = s / denom;
  }
  return out;
}

const rollingSum = (src, n) => {
  const s = sma(src, n);
  return s.map((v) => (ok(v) ? v * n : N));
};

export function stdev(src, n) {
  const out = new Array(src.length).fill(N);
  for (let i = n - 1; i < src.length; i += 1) {
    let s = 0;
    let s2 = 0;
    let good = true;
    for (let k = 0; k < n; k += 1) {
      const v = src[i - k];
      if (!ok(v)) {
        good = false;
        break;
      }
      s += v;
      s2 += v * v;
    }
    if (good) out[i] = Math.sqrt(Math.max(0, s2 / n - (s / n) ** 2));
  }
  return out;
}

const highest = (src, n) =>
  src.map((_, i) => {
    if (i < n - 1) return N;
    let m = -Infinity;
    for (let k = 0; k < n; k += 1) m = Math.max(m, src[i - k]);
    return ok(m) ? m : N;
  });
const lowest = (src, n) =>
  src.map((_, i) => {
    if (i < n - 1) return N;
    let m = Infinity;
    for (let k = 0; k < n; k += 1) m = Math.min(m, src[i - k]);
    return ok(m) ? m : N;
  });

const trueRange = (c) =>
  c.map((x, i) =>
    i === 0
      ? x.high - x.low
      : Math.max(x.high - x.low, Math.abs(x.high - c[i - 1].close), Math.abs(x.low - c[i - 1].close))
  );
const atr = (c, n) => rma(trueRange(c), n);

const shift = (src, k) => src.map((_, i) => (i - k >= 0 && i - k < src.length ? src[i - k] : N));
const sub = (a, b) => a.map((v, i) => v - b[i]);
const roc = (src, n) => src.map((v, i) => (i >= n && src[i - n] ? ((v - src[i - n]) / src[i - n]) * 100 : N));

/** Linear-regression fit over the last n values at each bar: { value (endpoint), slope, r2 }. */
function linreg(src, n) {
  const value = new Array(src.length).fill(N);
  const r2 = new Array(src.length).fill(N);
  const xm = (n - 1) / 2;
  let sxx = 0;
  for (let x = 0; x < n; x += 1) sxx += (x - xm) ** 2;
  for (let i = n - 1; i < src.length; i += 1) {
    let ym = 0;
    for (let x = 0; x < n; x += 1) ym += src[i - n + 1 + x];
    ym /= n;
    if (!ok(ym)) continue;
    let sxy = 0;
    let syy = 0;
    for (let x = 0; x < n; x += 1) {
      const y = src[i - n + 1 + x] - ym;
      sxy += (x - xm) * y;
      syy += y * y;
    }
    const slope = sxy / sxx;
    value[i] = ym + slope * (n - 1 - xm);
    r2[i] = syy > 0 ? (sxy * sxy) / (sxx * syy) : 0;
  }
  return { value, r2 };
}

const cmo = (src, n) => {
  const up = src.map((v, i) => (i ? Math.max(0, v - src[i - 1]) : N));
  const dn = src.map((v, i) => (i ? Math.max(0, src[i - 1] - v) : N));
  const su = rollingSum(up, n);
  const sd = rollingSum(dn, n);
  return su.map((u, i) => (ok(u) && u + sd[i] > 0 ? (100 * (u - sd[i])) / (u + sd[i]) : ok(u) ? 0 : N));
};

/** Bucket start (UTC seconds) of the day / week (Monday) / month containing `t`. */
function bucketStart(t, unit) {
  const d = new Date(t * 1000);
  if (unit === "month") return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 1000;
  const day = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000;
  if (unit === "week") return day - ((d.getUTCDay() + 6) % 7) * 86400;
  return day;
}
const barSeconds = (c) => {
  if (c.length < 2) return 60;
  const gaps = [];
  for (let i = 1; i < Math.min(c.length, 50); i += 1) gaps.push(c[i].time - c[i - 1].time);
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)] || 60;
};

const COLORS = {
  a: "#38bdf8",
  b: "#f59e0b",
  c: "#a78bfa",
  up: "#22c55e",
  down: "#ef4444",
  mute: "#94a3b8",
  pink: "#f472b6",
};

const line = (name, values, extra = {}) => ({ name, values, ...extra });
const P = (key, label, def, min = 1, step = 1) => ({ key, label, default: def, min, step });

// ---------- candlestick pattern helpers ----------
const body = (x) => Math.abs(x.close - x.open);
const range = (x) => Math.max(x.high - x.low, 1e-12);
const upperWick = (x) => x.high - Math.max(x.open, x.close);
const lowerWick = (x) => Math.min(x.open, x.close) - x.low;
const green = (x) => x.close > x.open;
const red = (x) => x.close < x.open;
const isDoji = (x) => body(x) <= 0.1 * range(x);
const mid = (x) => (x.open + x.close) / 2;
const upTrend = (c, i) => i >= 6 && c[i - 1].close > c[i - 6].close;
const downTrend = (c, i) => i >= 6 && c[i - 1].close < c[i - 6].close;
const avgBody = (c, i, n = 10) => {
  let s = 0;
  let k = 0;
  for (let j = Math.max(0, i - n); j < i; j += 1) {
    s += body(c[j]);
    k += 1;
  }
  return k ? s / k : body(c[i]);
};
const bigBody = (c, i) => body(c[i]) >= avgBody(c, i) * 0.9 && body(c[i]) >= 0.5 * range(c[i]);
const hammerShape = (x) =>
  body(x) > 0 && lowerWick(x) >= 2 * body(x) && upperWick(x) <= 0.15 * range(x);
const invHammerShape = (x) =>
  body(x) > 0 && upperWick(x) >= 2 * body(x) && lowerWick(x) <= 0.15 * range(x);

/** Builds a pattern indicator; `test(c, i)` returns true where the pattern completes at bar i. */
const pattern = (fullLabel, shortLabel, bias, test) => ({
  fullLabel,
  shortLabel,
  group: "patterns",
  overlay: true,
  paramDefs: [],
  compute: (c) => {
    const markers = [];
    for (let i = 0; i < c.length; i += 1) {
      if (!test(c, i)) continue;
      markers.push({
        time: c[i].time,
        position: bias === "bearish" ? "aboveBar" : bias === "bullish" ? "belowBar" : "aboveBar",
        shape: bias === "bearish" ? "arrowDown" : bias === "bullish" ? "arrowUp" : "circle",
        color: bias === "bearish" ? COLORS.down : bias === "bullish" ? COLORS.up : COLORS.mute,
        text: shortLabel,
        size: 0.8,
      });
    }
    return { lines: [], markers };
  },
});

const starTest = (bull, dojiMiddle) => (c, i) => {
  if (i < 2) return false;
  const [a, b, x] = [c[i - 2], c[i - 1], c[i]];
  const middleOk = dojiMiddle ? isDoji(b) : body(b) <= 0.3 * range(b) && !isDoji(b);
  if (!middleOk || !bigBody(c, i - 2)) return false;
  return bull
    ? red(a) && Math.max(b.open, b.close) <= a.close && green(x) && x.close > mid(a)
    : green(a) && Math.min(b.open, b.close) >= a.close && red(x) && x.close < mid(a);
};

// ---------- the catalog ----------
export const GO_INDICATORS = {
  // ===== Overlays =====
  atrBands: {
    fullLabel: "ATR Bands",
    shortLabel: "ATRB",
    group: "volatility",
    overlay: true,
    paramDefs: [P("period", "ATR period", 14), P("mult", "Multiplier", 2, 0.1, 0.1)],
    compute: (c, p) => {
      const a = atr(c, p.period);
      const cl = closes(c);
      return {
        lines: [
          line("Upper", cl.map((v, i) => v + p.mult * a[i])),
          line("Lower", cl.map((v, i) => v - p.mult * a[i])),
        ],
      };
    },
  },
  atrTrailingStop: {
    fullLabel: "ATR Trailing Stop",
    shortLabel: "ATRTS",
    group: "trend",
    overlay: true,
    paramDefs: [P("period", "ATR period", 14), P("mult", "Multiplier", 3, 0.1, 0.1)],
    compute: (c, p) => {
      const a = atr(c, p.period);
      const longStop = new Array(c.length).fill(N);
      const shortStop = new Array(c.length).fill(N);
      let stop = N;
      let long = true;
      for (let i = 0; i < c.length; i += 1) {
        if (!ok(a[i])) continue;
        const loss = p.mult * a[i];
        const cl = c[i].close;
        if (!ok(stop)) {
          stop = cl - loss;
          long = true;
        } else if (long) {
          if (cl < stop) {
            long = false;
            stop = cl + loss;
          } else stop = Math.max(stop, cl - loss);
        } else if (cl > stop) {
          long = true;
          stop = cl - loss;
        } else stop = Math.min(stop, cl + loss);
        if (long) longStop[i] = stop;
        else shortStop[i] = stop;
      }
      return {
        lines: [
          line("Long stop", longStop, { color: COLORS.up }),
          line("Short stop", shortStop, { color: COLORS.down }),
        ],
      };
    },
  },
  alligator: {
    fullLabel: "Alligator (Williams)",
    shortLabel: "ALLI",
    group: "trend",
    overlay: true,
    paramDefs: [P("jaw", "Jaw", 13), P("teeth", "Teeth", 8), P("lips", "Lips", 5)],
    compute: (c, p) => {
      const m = hl2(c);
      return {
        lines: [
          line(`Jaw ${p.jaw}`, shift(rma(m, p.jaw), 8), { color: "#3b82f6" }),
          line(`Teeth ${p.teeth}`, shift(rma(m, p.teeth), 5), { color: "#ef4444" }),
          line(`Lips ${p.lips}`, shift(rma(m, p.lips), 3), { color: "#22c55e" }),
        ],
      };
    },
  },
  fractals: {
    fullLabel: "Fractals (Williams)",
    shortLabel: "FRAC",
    group: "trend",
    overlay: true,
    paramDefs: [],
    compute: (c) => {
      const markers = [];
      for (let i = 2; i < c.length - 2; i += 1) {
        const h = c[i].high;
        const l = c[i].low;
        if (h > c[i - 1].high && h > c[i - 2].high && h > c[i + 1].high && h > c[i + 2].high) {
          markers.push({ time: c[i].time, position: "aboveBar", shape: "arrowDown", color: COLORS.down, size: 0.6 });
        }
        if (l < c[i - 1].low && l < c[i - 2].low && l < c[i + 1].low && l < c[i + 2].low) {
          markers.push({ time: c[i].time, position: "belowBar", shape: "arrowUp", color: COLORS.up, size: 0.6 });
        }
      }
      return { lines: [], markers };
    },
  },
  guppyMma: {
    fullLabel: "Guppy Multiple Moving Average",
    shortLabel: "GMMA",
    group: "trend",
    overlay: true,
    paramDefs: [],
    compute: (c) => {
      const cl = closes(c);
      return {
        lines: [
          ...[3, 5, 8, 10, 12, 15].map((n) => line(`EMA ${n}`, ema(cl, n), { color: "#38bdf8", lineWidth: 1 })),
          ...[30, 35, 40, 45, 50, 60].map((n) => line(`EMA ${n}`, ema(cl, n), { color: "#f87171", lineWidth: 1 })),
        ],
      };
    },
  },
  maEnvelopes: {
    fullLabel: "Moving Average Envelopes",
    shortLabel: "ENV",
    group: "volatility",
    overlay: true,
    paramDefs: [P("period", "Period", 20), P("pct", "Percent", 2.5, 0.01, 0.1)],
    compute: (c, p) => {
      const m = sma(closes(c), p.period);
      return {
        lines: [
          line("Upper", m.map((v) => v * (1 + p.pct / 100))),
          line("Basis", m, { lineStyle: 2 }),
          line("Lower", m.map((v) => v * (1 - p.pct / 100))),
        ],
      };
    },
  },
  pivotPoints: {
    fullLabel: "Pivot Points (Classic)",
    shortLabel: "PIVOT",
    group: "trend",
    overlay: true,
    paramDefs: [],
    compute: (c) => {
      const sec = barSeconds(c);
      const unit = sec >= 86400 * 7 ? "month" : sec >= 86400 ? "week" : "day";
      const names = ["P", "R1", "S1", "R2", "S2", "R3", "S3"];
      const out = names.map(() => new Array(c.length).fill(N));
      let curKey = null;
      let cur = null;
      let prev = null;
      for (let i = 0; i < c.length; i += 1) {
        const key = bucketStart(c[i].time, unit);
        if (key !== curKey) {
          prev = cur;
          cur = { h: c[i].high, l: c[i].low, c: c[i].close };
          curKey = key;
        } else {
          cur.h = Math.max(cur.h, c[i].high);
          cur.l = Math.min(cur.l, c[i].low);
          cur.c = c[i].close;
        }
        if (!prev) continue;
        const pp = (prev.h + prev.l + prev.c) / 3;
        const r = prev.h - prev.l;
        const vals = [pp, 2 * pp - prev.l, 2 * pp - prev.h, pp + r, pp - r, prev.h + 2 * (pp - prev.l), prev.l - 2 * (prev.h - pp)];
        vals.forEach((v, k) => {
          out[k][i] = v;
        });
      }
      const col = { P: "#eab308", R1: COLORS.down, R2: COLORS.down, R3: COLORS.down, S1: COLORS.up, S2: COLORS.up, S3: COLORS.up };
      return {
        lines: names.map((n, k) => line(n, out[k], { color: col[n], lineWidth: 1, lineStyle: n === "P" ? 0 : 2, step: true })),
      };
    },
  },
  smma: {
    fullLabel: "Smoothed Moving Average",
    shortLabel: "SMMA",
    group: "trend",
    overlay: true,
    paramDefs: [P("period", "Period", 14)],
    compute: (c, p) => ({ lines: [line(`SMMA ${p.period}`, rma(closes(c), p.period))] }),
  },
  starcBands: {
    fullLabel: "STARC Bands",
    shortLabel: "STARC",
    group: "volatility",
    overlay: true,
    paramDefs: [P("maPeriod", "SMA period", 6), P("atrPeriod", "ATR period", 15), P("mult", "Multiplier", 2, 0.1, 0.1)],
    compute: (c, p) => {
      const m = sma(closes(c), p.maPeriod);
      const a = atr(c, p.atrPeriod);
      return {
        lines: [
          line("Upper", m.map((v, i) => v + p.mult * a[i])),
          line("Basis", m, { lineStyle: 2 }),
          line("Lower", m.map((v, i) => v - p.mult * a[i])),
        ],
      };
    },
  },
  supertrend: {
    fullLabel: "Supertrend",
    shortLabel: "ST",
    group: "trend",
    overlay: true,
    paramDefs: [P("period", "ATR period", 10), P("mult", "Multiplier", 3, 0.1, 0.1)],
    compute: (c, p) => {
      const a = atr(c, p.period);
      const upLine = new Array(c.length).fill(N);
      const dnLine = new Array(c.length).fill(N);
      let fu = N;
      let fl = N;
      let trendUp = true;
      for (let i = 0; i < c.length; i += 1) {
        if (!ok(a[i])) continue;
        const m = (c[i].high + c[i].low) / 2;
        const bu = m + p.mult * a[i];
        const bl = m - p.mult * a[i];
        const pc = i ? c[i - 1].close : c[i].close;
        fu = !ok(fu) || bu < fu || pc > fu ? bu : fu;
        fl = !ok(fl) || bl > fl || pc < fl ? bl : fl;
        if (trendUp && c[i].close < fl) trendUp = false;
        else if (!trendUp && c[i].close > fu) trendUp = true;
        if (trendUp) upLine[i] = fl;
        else dnLine[i] = fu;
      }
      return {
        lines: [line("Up", upLine, { color: COLORS.up }), line("Down", dnLine, { color: COLORS.down })],
      };
    },
  },
  tdSequential: {
    fullLabel: "TD Sequential",
    shortLabel: "TD",
    group: "trend",
    overlay: true,
    paramDefs: [],
    compute: (c) => {
      const markers = [];
      let buy = 0;
      let sell = 0;
      let buyCd = -1;
      let sellCd = -1;
      for (let i = 4; i < c.length; i += 1) {
        buy = c[i].close < c[i - 4].close ? buy + 1 : 0;
        sell = c[i].close > c[i - 4].close ? sell + 1 : 0;
        if (buy === 9) {
          markers.push({ time: c[i].time, position: "belowBar", shape: "arrowUp", color: COLORS.up, text: "9", size: 0.7 });
          buyCd = 0;
          sellCd = -1;
        }
        if (sell === 9) {
          markers.push({ time: c[i].time, position: "aboveBar", shape: "arrowDown", color: COLORS.down, text: "9", size: 0.7 });
          sellCd = 0;
          buyCd = -1;
        }
        if (buyCd >= 0 && i >= 2 && c[i].close <= c[i - 2].low) {
          buyCd += 1;
          if (buyCd === 13) {
            markers.push({ time: c[i].time, position: "belowBar", shape: "circle", color: COLORS.up, text: "13", size: 0.7 });
            buyCd = -1;
          }
        }
        if (sellCd >= 0 && i >= 2 && c[i].close >= c[i - 2].high) {
          sellCd += 1;
          if (sellCd === 13) {
            markers.push({ time: c[i].time, position: "aboveBar", shape: "circle", color: COLORS.down, text: "13", size: 0.7 });
            sellCd = -1;
          }
        }
      }
      return { lines: [], markers };
    },
  },
  trima: {
    fullLabel: "Triangular Moving Average",
    shortLabel: "TRIMA",
    group: "trend",
    overlay: true,
    paramDefs: [P("period", "Period", 20, 2)],
    compute: (c, p) => {
      const n1 = Math.ceil((p.period + 1) / 2);
      const n2 = Math.floor((p.period + 1) / 2) || 1;
      return { lines: [line(`TRIMA ${p.period}`, sma(sma(closes(c), n1), n2))] };
    },
  },
  tsma: {
    fullLabel: "Time Series Moving Average",
    shortLabel: "TSMA",
    group: "trend",
    overlay: true,
    paramDefs: [P("period", "Period", 14, 2)],
    compute: (c, p) => ({ lines: [line(`TSMA ${p.period}`, linreg(closes(c), p.period).value)] }),
  },
  vidya: {
    fullLabel: "VIDYA Moving Average",
    shortLabel: "VIDYA",
    group: "trend",
    overlay: true,
    paramDefs: [P("period", "Period", 14), P("cmo", "CMO period", 9)],
    compute: (c, p) => {
      const cl = closes(c);
      const k = cmo(cl, p.cmo);
      const alpha = 2 / (p.period + 1);
      const out = new Array(c.length).fill(N);
      let prev = N;
      for (let i = 0; i < c.length; i += 1) {
        if (!ok(k[i])) continue;
        const a = (alpha * Math.abs(k[i])) / 100;
        prev = ok(prev) ? a * cl[i] + (1 - a) * prev : cl[i];
        out[i] = prev;
      }
      return { lines: [line(`VIDYA ${p.period}`, out)] };
    },
  },
  vma: {
    fullLabel: "Variable Moving Average",
    shortLabel: "VMA",
    group: "trend",
    overlay: true,
    paramDefs: [P("period", "Period", 9, 2)],
    compute: (c, p) => {
      const cl = closes(c);
      const n = p.period;
      const alpha = 2 / (n + 1);
      const out = new Array(c.length).fill(N);
      let prev = N;
      for (let i = n; i < c.length; i += 1) {
        let noise = 0;
        for (let k = 0; k < n; k += 1) noise += Math.abs(cl[i - k] - cl[i - k - 1]);
        const er = noise > 0 ? Math.abs(cl[i] - cl[i - n]) / noise : 0;
        prev = ok(prev) ? alpha * er * cl[i] + (1 - alpha * er) * prev : cl[i];
        out[i] = prev;
      }
      return { lines: [line(`VMA ${n}`, out)] };
    },
  },
  vwma: {
    fullLabel: "Volume Weighted Moving Average",
    shortLabel: "VWMA",
    group: "trend",
    overlay: true,
    paramDefs: [P("period", "Period", 20)],
    compute: (c, p) => {
      const pv = rollingSum(c.map((x) => x.close * vol(x)), p.period);
      const v = rollingSum(vols(c), p.period);
      return { lines: [line(`VWMA ${p.period}`, pv.map((x, i) => x / v[i]))] };
    },
  },
  vwapBands: {
    fullLabel: "VWAP Bands",
    shortLabel: "VWAPB",
    group: "volume",
    overlay: true,
    paramDefs: [P("mult1", "Band 1 ×σ", 1, 0.1, 0.1), P("mult2", "Band 2 ×σ", 2, 0.1, 0.1)],
    compute: (c, p) => {
      const names = ["VWAP", "+1", "-1", "+2", "-2"];
      const out = names.map(() => new Array(c.length).fill(N));
      let day = null;
      let sv = 0;
      let spv = 0;
      let sp2v = 0;
      for (let i = 0; i < c.length; i += 1) {
        const d = bucketStart(c[i].time, "day");
        if (d !== day) {
          day = d;
          sv = 0;
          spv = 0;
          sp2v = 0;
        }
        const tp = (c[i].high + c[i].low + c[i].close) / 3;
        const v = vol(c[i]);
        sv += v;
        spv += tp * v;
        sp2v += tp * tp * v;
        const vw = spv / sv;
        const sd = Math.sqrt(Math.max(0, sp2v / sv - vw * vw));
        [vw, vw + p.mult1 * sd, vw - p.mult1 * sd, vw + p.mult2 * sd, vw - p.mult2 * sd].forEach((v2, k) => {
          out[k][i] = v2;
        });
      }
      return {
        lines: names.map((n, k) =>
          line(n, out[k], { color: k === 0 ? "#eab308" : "#a78bfa", lineWidth: k === 0 ? 2 : 1, lineStyle: k ? 2 : 0 })
        ),
      };
    },
  },
  anchoredVwap: {
    fullLabel: "Anchor VWAP and Bands",
    shortLabel: "AVWAP",
    group: "volume",
    overlay: true,
    paramDefs: [P("barsBack", "Anchor (bars back)", 100), P("mult1", "Band 1 ×σ", 1, 0, 0.1), P("mult2", "Band 2 ×σ", 2, 0, 0.1)],
    compute: (c, p) => {
      const names = ["AVWAP", "+1", "-1", "+2", "-2"];
      const out = names.map(() => new Array(c.length).fill(N));
      const start = Math.max(0, c.length - p.barsBack);
      let sv = 0;
      let spv = 0;
      let sp2v = 0;
      for (let i = start; i < c.length; i += 1) {
        const v = vol(c[i]);
        const tp = (c[i].high + c[i].low + c[i].close) / 3;
        sv += v;
        spv += tp * v;
        sp2v += tp * tp * v;
        const vw = spv / sv;
        const sd = Math.sqrt(Math.max(0, sp2v / sv - vw * vw));
        [vw, vw + p.mult1 * sd, vw - p.mult1 * sd, vw + p.mult2 * sd, vw - p.mult2 * sd].forEach((x, k) => {
          out[k][i] = x;
        });
      }
      return {
        lines: names.map((n, k) =>
          line(n, out[k], { color: k === 0 ? "#eab308" : "#a78bfa", lineWidth: k === 0 ? 2 : 1, lineStyle: k ? 2 : 0 })
        ),
      };
    },
  },
  highestHigh: {
    fullLabel: "Highest High Value",
    shortLabel: "HHV",
    group: "volatility",
    overlay: true,
    paramDefs: [P("period", "Period", 20)],
    compute: (c, p) => ({ lines: [line(`HHV ${p.period}`, highest(highs(c), p.period), { color: COLORS.up })] }),
  },
  lowestLow: {
    fullLabel: "Lowest Low Value",
    shortLabel: "LLV",
    group: "volatility",
    overlay: true,
    paramDefs: [P("period", "Period", 20)],
    compute: (c, p) => ({ lines: [line(`LLV ${p.period}`, lowest(lows(c), p.period), { color: COLORS.down })] }),
  },
  medianPrice: {
    fullLabel: "Median Price",
    shortLabel: "MEDP",
    group: "trend",
    overlay: true,
    paramDefs: [],
    compute: (c) => ({ lines: [line("Median", hl2(c))] }),
  },
  typicalPrice: {
    fullLabel: "Typical Price",
    shortLabel: "TYPP",
    group: "trend",
    overlay: true,
    paramDefs: [],
    compute: (c) => ({ lines: [line("Typical", hlc3(c))] }),
  },
  weightedClose: {
    fullLabel: "Weighted Close",
    shortLabel: "WCL",
    group: "trend",
    overlay: true,
    paramDefs: [],
    compute: (c) => ({ lines: [line("Weighted close", c.map((x) => (x.high + x.low + 2 * x.close) / 4))] }),
  },
  moonPhases: {
    fullLabel: "Moon Phases",
    shortLabel: "MOON",
    group: "patterns",
    overlay: true,
    paramDefs: [],
    compute: (c) => {
      const SYNODIC = 29.530588853 * 86400;
      const REF_NEW = Date.UTC(2000, 0, 6, 18, 14) / 1000; // a known new moon
      const phaseAt = (t) => ((((t - REF_NEW) % SYNODIC) + SYNODIC) % SYNODIC) / SYNODIC;
      const markers = [];
      for (let i = 1; i < c.length; i += 1) {
        const a = phaseAt(c[i - 1].time);
        const b = phaseAt(c[i].time);
        if (b < a) {
          markers.push({ time: c[i].time, position: "aboveBar", shape: "circle", color: "#64748b", text: "New moon", size: 0.7 });
        } else if (a < 0.5 && b >= 0.5) {
          markers.push({ time: c[i].time, position: "aboveBar", shape: "circle", color: "#fde68a", text: "Full moon", size: 0.7 });
        }
      }
      return { lines: [], markers };
    },
  },
  volumeUnderlay: {
    fullLabel: "Volume Underlay",
    shortLabel: "VOL",
    group: "volume",
    overlay: true,
    paramDefs: [],
    compute: (c) => ({
      lines: [
        line("Volume", vols(c), {
          kind: "histogram",
          colorBy: "candle",
          priceScaleId: "go-volume-underlay",
          scaleMargins: { top: 0.8, bottom: 0 },
        }),
      ],
    }),
  },

  // ===== Oscillators (own pane) =====
  aroon: {
    fullLabel: "Aroon",
    shortLabel: "AROON",
    group: "momentum",
    paramDefs: [P("period", "Period", 14)],
    compute: (c, p) => {
      const n = p.period;
      const up = new Array(c.length).fill(N);
      const dn = new Array(c.length).fill(N);
      for (let i = n; i < c.length; i += 1) {
        let hi = i;
        let lo = i;
        for (let k = i - n; k <= i; k += 1) {
          if (c[k].high >= c[hi].high) hi = k;
          if (c[k].low <= c[lo].low) lo = k;
        }
        up[i] = (100 * (n - (i - hi))) / n;
        dn[i] = (100 * (n - (i - lo))) / n;
      }
      return { lines: [line("Up", up, { color: COLORS.up }), line("Down", dn, { color: COLORS.down })], levels: [70, 30] };
    },
  },
  aroonOsc: {
    fullLabel: "Aroon Oscillator",
    shortLabel: "AROONO",
    group: "momentum",
    paramDefs: [P("period", "Period", 14)],
    compute: (c, p) => {
      const r = GO_INDICATORS.aroon.compute(c, p);
      return { lines: [line("Aroon Osc", sub(r.lines[0].values, r.lines[1].values))], levels: [0] };
    },
  },
  balanceOfPower: {
    fullLabel: "Balance of Power",
    shortLabel: "BOP",
    group: "momentum",
    paramDefs: [P("period", "Smoothing", 14)],
    compute: (c, p) => ({
      lines: [line("BOP", sma(c.map((x) => (x.close - x.open) / range(x)), p.period), { kind: "histogram", colorBy: "sign" })],
      levels: [0],
    }),
  },
  bbPercentB: {
    fullLabel: "Bollinger Bands %B",
    shortLabel: "%B",
    group: "volatility",
    paramDefs: [P("period", "Period", 20), P("mult", "Std dev", 2, 0.1, 0.1)],
    compute: (c, p) => {
      const cl = closes(c);
      const m = sma(cl, p.period);
      const s = stdev(cl, p.period);
      return {
        lines: [line("%B", cl.map((v, i) => (s[i] > 0 ? (v - (m[i] - p.mult * s[i])) / (2 * p.mult * s[i]) : N)))],
        levels: [1, 0.5, 0],
      };
    },
  },
  boringCandle: {
    fullLabel: "Boring Candle",
    shortLabel: "BORING",
    group: "patterns",
    overlay: true,
    paramDefs: [P("pct", "Max body % of range", 50, 1, 1)],
    compute: (c, p) => ({
      lines: [],
      markers: c
        .filter((x) => body(x) <= (p.pct / 100) * range(x))
        .map((x) => ({ time: x.time, position: "aboveBar", shape: "square", color: "#eab308", size: 0.4 })),
    }),
  },
  centerOfGravity: {
    fullLabel: "Center of Gravity",
    shortLabel: "COG",
    group: "momentum",
    paramDefs: [P("period", "Period", 10, 2)],
    compute: (c, p) => {
      const m = hl2(c);
      const out = m.map((_, i) => {
        if (i < p.period - 1) return N;
        let num = 0;
        let den = 0;
        for (let k = 0; k < p.period; k += 1) {
          num += (k + 1) * m[i - k];
          den += m[i - k];
        }
        return den ? -num / den : N;
      });
      return { lines: [line("COG", out), line("Signal", shift(out, 1), { color: COLORS.b })] };
    },
  },
  chandeForecast: {
    fullLabel: "Chande Forecast Oscillator",
    shortLabel: "CFO",
    group: "momentum",
    paramDefs: [P("period", "Period", 14, 2)],
    compute: (c, p) => {
      const cl = closes(c);
      const f = linreg(cl, p.period).value;
      return { lines: [line("CFO", cl.map((v, i) => ((v - f[i]) / v) * 100))], levels: [0] };
    },
  },
  choppiness: {
    fullLabel: "Choppiness Index",
    shortLabel: "CHOP",
    group: "volatility",
    paramDefs: [P("period", "Period", 14, 2)],
    compute: (c, p) => {
      const sumTr = rollingSum(trueRange(c), p.period);
      const hh = highest(highs(c), p.period);
      const ll = lowest(lows(c), p.period);
      return {
        lines: [line("CHOP", sumTr.map((s, i) => (hh[i] > ll[i] ? (100 * Math.log10(s / (hh[i] - ll[i]))) / Math.log10(p.period) : N)))],
        levels: [61.8, 38.2],
      };
    },
  },
  coppock: {
    fullLabel: "Coppock Curve",
    shortLabel: "COPP",
    group: "momentum",
    paramDefs: [P("wma", "WMA", 10), P("long", "Long ROC", 14), P("short", "Short ROC", 11)],
    compute: (c, p) => {
      const cl = closes(c);
      const r1 = roc(cl, p.long);
      const r2 = roc(cl, p.short);
      return { lines: [line("Coppock", wma(r1.map((v, i) => v + r2[i]), p.wma), { kind: "histogram", colorBy: "sign" })], levels: [0] };
    },
  },
  dpo: {
    fullLabel: "Detrended Price Oscillator",
    shortLabel: "DPO",
    group: "momentum",
    paramDefs: [P("period", "Period", 21, 2)],
    compute: (c, p) => {
      const cl = closes(c);
      const m = sma(cl, p.period);
      const back = Math.floor(p.period / 2) + 1;
      return { lines: [line("DPO", cl.map((_, i) => (i >= back ? cl[i - back] - m[i] : N)))], levels: [0] };
    },
  },
  disparity: {
    fullLabel: "Disparity Index",
    shortLabel: "DI",
    group: "momentum",
    paramDefs: [P("period", "Period", 14)],
    compute: (c, p) => {
      const cl = closes(c);
      const m = sma(cl, p.period);
      return { lines: [line("Disparity", cl.map((v, i) => (v / m[i] - 1) * 100))], levels: [0] };
    },
  },
  donchianWidth: {
    fullLabel: "Donchian Width",
    shortLabel: "DCW",
    group: "volatility",
    paramDefs: [P("period", "Period", 20)],
    compute: (c, p) => ({ lines: [line("Width", sub(highest(highs(c), p.period), lowest(lows(c), p.period)))] }),
  },
  easeOfMovement: {
    fullLabel: "Ease of Movement",
    shortLabel: "EOM",
    group: "volume",
    paramDefs: [P("period", "Period", 14), P("divisor", "Volume divisor", 10000)],
    compute: (c, p) => {
      const raw = c.map((x, i) => {
        if (!i) return N;
        const dm = (x.high + x.low) / 2 - (c[i - 1].high + c[i - 1].low) / 2;
        const box = vol(x) / p.divisor / range(x);
        return box ? dm / box : 0;
      });
      return { lines: [line("EOM", sma(raw, p.period))], levels: [0] };
    },
  },
  guppyOsc: {
    fullLabel: "Guppy Oscillator",
    shortLabel: "GUPPYO",
    group: "trend",
    paramDefs: [P("signal", "Signal", 13)],
    compute: (c, p) => {
      const cl = closes(c);
      const avg = (ns) => {
        const es = ns.map((n) => ema(cl, n));
        return cl.map((_, i) => es.reduce((s, e) => s + e[i], 0) / ns.length);
      };
      const s = avg([3, 5, 8, 10, 12, 15]);
      const l = avg([30, 35, 40, 45, 50, 60]);
      const osc = s.map((v, i) => ((v - l[i]) / l[i]) * 100);
      return {
        lines: [line("Guppy", osc, { kind: "histogram", colorBy: "sign" }), line("Signal", ema(osc, p.signal), { color: COLORS.b })],
        levels: [0],
      };
    },
  },
  gator: {
    fullLabel: "Gator Oscillator",
    shortLabel: "GATOR",
    group: "trend",
    paramDefs: [],
    compute: (c) => {
      const [jaw, teeth, lips] = GO_INDICATORS.alligator.compute(c, { jaw: 13, teeth: 8, lips: 5 }).lines.map((l) => l.values);
      return {
        lines: [
          line("Upper", jaw.map((v, i) => Math.abs(v - teeth[i])), { kind: "histogram", colorBy: "rising" }),
          line("Lower", teeth.map((v, i) => -Math.abs(v - lips[i])), { kind: "histogram", colorBy: "falling" }),
        ],
        levels: [0],
      };
    },
  },
  gapo: {
    fullLabel: "Gopalakrishnan Range Index",
    shortLabel: "GAPO",
    group: "volatility",
    paramDefs: [P("period", "Period", 5, 2)],
    compute: (c, p) => {
      const r = sub(highest(highs(c), p.period), lowest(lows(c), p.period));
      return { lines: [line("GAPO", r.map((v) => (v > 0 ? Math.log(v) / Math.log(p.period) : N)))] };
    },
  },
  highMinusLow: {
    fullLabel: "High Minus Low",
    shortLabel: "H-L",
    group: "volatility",
    paramDefs: [],
    compute: (c) => ({ lines: [line("High − Low", c.map(range), { kind: "histogram" })] }),
  },
  historicalVol: {
    fullLabel: "Historical Volatility",
    shortLabel: "HV",
    group: "volatility",
    paramDefs: [P("period", "Period", 20, 2), P("annual", "Periods per year", 252)],
    compute: (c, p) => {
      const lr = c.map((x, i) => (i ? Math.log(x.close / c[i - 1].close) : N));
      return { lines: [line("HV %", stdev(lr, p.period).map((s) => s * Math.sqrt(p.annual) * 100))] };
    },
  },
  imi: {
    fullLabel: "Intraday Momentum Index",
    shortLabel: "IMI",
    group: "momentum",
    paramDefs: [P("period", "Period", 14)],
    compute: (c, p) => {
      const g = rollingSum(c.map((x) => Math.max(0, x.close - x.open)), p.period);
      const l = rollingSum(c.map((x) => Math.max(0, x.open - x.close)), p.period);
      return { lines: [line("IMI", g.map((v, i) => (v + l[i] > 0 ? (100 * v) / (v + l[i]) : 50)))], levels: [70, 30] };
    },
  },
  klinger: {
    fullLabel: "Klinger Oscillator",
    shortLabel: "KVO",
    group: "volume",
    paramDefs: [P("fast", "Fast", 34), P("slow", "Slow", 55), P("signal", "Signal", 13)],
    compute: (c, p) => {
      const tp = hlc3(c);
      const vf = new Array(c.length).fill(N);
      let trend = 0;
      let cm = 0;
      let prevDm = 0;
      for (let i = 1; i < c.length; i += 1) {
        const t = tp[i] > tp[i - 1] ? 1 : -1;
        const dm = c[i].high - c[i].low;
        cm = t === trend ? cm + dm : prevDm + dm;
        trend = t;
        prevDm = dm;
        vf[i] = cm ? vol(c[i]) * Math.abs(2 * (dm / cm) - 1) * t * 100 : 0;
      }
      const kvo = sub(ema(vf, p.fast), ema(vf, p.slow));
      return { lines: [line("KVO", kvo), line("Signal", ema(kvo, p.signal), { color: COLORS.b })], levels: [0] };
    },
  },
  kst: {
    fullLabel: "Know Sure Thing",
    shortLabel: "KST",
    group: "momentum",
    paramDefs: [P("signal", "Signal", 9)],
    compute: (c, p) => {
      const cl = closes(c);
      const r = [
        [10, 10],
        [15, 10],
        [20, 10],
        [30, 15],
      ].map(([rn, sn]) => sma(roc(cl, rn), sn));
      const kst = cl.map((_, i) => r[0][i] + 2 * r[1][i] + 3 * r[2][i] + 4 * r[3][i]);
      return { lines: [line("KST", kst), line("Signal", sma(kst, p.signal), { color: COLORS.b })], levels: [0] };
    },
  },
  massIndex: {
    fullLabel: "Mass Index",
    shortLabel: "MASS",
    group: "volatility",
    paramDefs: [P("period", "Sum period", 25), P("ema", "EMA", 9)],
    compute: (c, p) => {
      const e1 = ema(c.map(range), p.ema);
      const e2 = ema(e1, p.ema);
      return { lines: [line("Mass", rollingSum(e1.map((v, i) => v / e2[i]), p.period))], levels: [27, 26.5] };
    },
  },
  nvi: {
    fullLabel: "Negative Volume Index",
    shortLabel: "NVI",
    group: "volume",
    paramDefs: [P("signal", "Signal EMA", 255)],
    compute: (c, p) => {
      const out = new Array(c.length).fill(N);
      let v = 1000;
      for (let i = 0; i < c.length; i += 1) {
        if (i && vol(c[i]) < vol(c[i - 1])) v += v * ((c[i].close - c[i - 1].close) / c[i - 1].close);
        out[i] = v;
      }
      return { lines: [line("NVI", out), line("Signal", ema(out, p.signal), { color: COLORS.b })] };
    },
  },
  pvi: {
    fullLabel: "Positive Volume Index",
    shortLabel: "PVI",
    group: "volume",
    paramDefs: [P("signal", "Signal EMA", 255)],
    compute: (c, p) => {
      const out = new Array(c.length).fill(N);
      let v = 1000;
      for (let i = 0; i < c.length; i += 1) {
        if (i && vol(c[i]) > vol(c[i - 1])) v += v * ((c[i].close - c[i - 1].close) / c[i - 1].close);
        out[i] = v;
      }
      return { lines: [line("PVI", out), line("Signal", ema(out, p.signal), { color: COLORS.b })] };
    },
  },
  prettyGood: {
    fullLabel: "Pretty Good Oscillator",
    shortLabel: "PGO",
    group: "momentum",
    paramDefs: [P("period", "Period", 14)],
    compute: (c, p) => {
      const cl = closes(c);
      const m = sma(cl, p.period);
      const e = ema(trueRange(c), p.period);
      return { lines: [line("PGO", cl.map((v, i) => (v - m[i]) / e[i]))], levels: [3, 0, -3] };
    },
  },
  pmo: {
    fullLabel: "Price Momentum Oscillator",
    shortLabel: "PMO",
    group: "momentum",
    paramDefs: [P("first", "First smoothing", 35), P("second", "Second smoothing", 20), P("signal", "Signal", 10)],
    compute: (c, p) => {
      const r1 = roc(closes(c), 1);
      const s1 = ema(r1, p.first, 2 / p.first).map((v) => v * 10);
      const pmo = ema(s1, p.second, 2 / p.second);
      return { lines: [line("PMO", pmo), line("Signal", ema(pmo, p.signal), { color: COLORS.b })], levels: [0] };
    },
  },
  ppo: {
    fullLabel: "Price Oscillator (PPO)",
    shortLabel: "PPO",
    group: "momentum",
    paramDefs: [P("fast", "Fast", 12), P("slow", "Slow", 26), P("signal", "Signal", 9)],
    compute: (c, p) => {
      const cl = closes(c);
      const f = ema(cl, p.fast);
      const s = ema(cl, p.slow);
      const ppo = f.map((v, i) => ((v - s[i]) / s[i]) * 100);
      const sig = ema(ppo, p.signal);
      return {
        lines: [
          line("Histogram", ppo.map((v, i) => v - sig[i]), { kind: "histogram", colorBy: "sign" }),
          line("PPO", ppo),
          line("Signal", sig, { color: COLORS.b }),
        ],
        levels: [0],
      };
    },
  },
  ravi: {
    fullLabel: "RAVI",
    shortLabel: "RAVI",
    group: "trend",
    paramDefs: [P("fast", "Fast", 7), P("slow", "Slow", 65)],
    compute: (c, p) => {
      const cl = closes(c);
      const f = sma(cl, p.fast);
      const s = sma(cl, p.slow);
      return { lines: [line("RAVI", f.map((v, i) => (Math.abs(v - s[i]) / s[i]) * 100))], levels: [3] };
    },
  },
  tvi: {
    fullLabel: "Trade Volume Index",
    shortLabel: "TVI",
    group: "volume",
    paramDefs: [P("mtv", "Min tick value", 0, 0, 0.0001)],
    compute: (c, p) => {
      const out = new Array(c.length).fill(N);
      let dir = 1;
      let t = 0;
      for (let i = 1; i < c.length; i += 1) {
        const ch = c[i].close - c[i - 1].close;
        if (ch > p.mtv) dir = 1;
        else if (ch < -p.mtv) dir = -1;
        t += dir * vol(c[i]);
        out[i] = t;
      }
      return { lines: [line("TVI", out)] };
    },
  },
  trix: {
    fullLabel: "TRIX",
    shortLabel: "TRIX",
    group: "momentum",
    paramDefs: [P("period", "Period", 15), P("signal", "Signal", 9)],
    compute: (c, p) => {
      const e3 = ema(ema(ema(closes(c), p.period), p.period), p.period);
      const t = roc(e3, 1);
      return { lines: [line("TRIX", t), line("Signal", ema(t, p.signal), { color: COLORS.b })], levels: [0] };
    },
  },
  twiggs: {
    fullLabel: "Twiggs Money Flow",
    shortLabel: "TMF",
    group: "volume",
    paramDefs: [P("period", "Period", 21)],
    compute: (c, p) => {
      const ad = c.map((x, i) => {
        if (!i) return N;
        const trh = Math.max(x.high, c[i - 1].close);
        const trl = Math.min(x.low, c[i - 1].close);
        const r = trh - trl;
        return r > 0 ? (vol(x) * (x.close - trl - (trh - x.close))) / r : 0;
      });
      const v = vols(c).map((x, i) => (i ? x : N));
      const a = rma(ad, p.period);
      const b = rma(v, p.period);
      return { lines: [line("TMF", a.map((x, i) => x / b[i]), { kind: "histogram", colorBy: "sign" })], levels: [0] };
    },
  },
  ulcer: {
    fullLabel: "Ulcer Index",
    shortLabel: "ULCER",
    group: "volatility",
    paramDefs: [P("period", "Period", 14)],
    compute: (c, p) => {
      const cl = closes(c);
      const mx = highest(cl, p.period);
      const dd2 = cl.map((v, i) => (((v - mx[i]) / mx[i]) * 100) ** 2);
      return { lines: [line("Ulcer", sma(dd2, p.period).map(Math.sqrt))] };
    },
  },

  // ===== Momentum (own pane) =====
  asi: {
    fullLabel: "Accumulative Swing Index",
    shortLabel: "ASI",
    group: "momentum",
    paramDefs: [P("limitAtr", "Limit move (× ATR14)", 3, 0.1, 0.1)],
    compute: (c, p) => {
      const si = swingIndex(c, p.limitAtr);
      let s = 0;
      return { lines: [line("ASI", si.map((v) => (ok(v) ? (s += v) : N)))] };
    },
  },
  swingIndex: {
    fullLabel: "Swing Index",
    shortLabel: "SI",
    group: "momentum",
    paramDefs: [P("limitAtr", "Limit move (× ATR14)", 3, 0.1, 0.1)],
    compute: (c, p) => ({ lines: [line("SI", swingIndex(c, p.limitAtr), { kind: "histogram", colorBy: "sign" })], levels: [0] }),
  },
  cmoOsc: {
    fullLabel: "Chande Momentum Oscillator",
    shortLabel: "CMO",
    group: "momentum",
    paramDefs: [P("period", "Period", 9)],
    compute: (c, p) => ({ lines: [line("CMO", cmo(closes(c), p.period))], levels: [50, 0, -50] }),
  },
  fisher: {
    fullLabel: "Ehlers Fisher Transform",
    shortLabel: "FISH",
    group: "momentum",
    paramDefs: [P("period", "Period", 9, 2)],
    compute: (c, p) => {
      const m = hl2(c);
      const hh = highest(m, p.period);
      const ll = lowest(m, p.period);
      const fish = new Array(c.length).fill(N);
      let v = 0;
      let f = 0;
      for (let i = 0; i < c.length; i += 1) {
        if (!ok(hh[i])) continue;
        const r = hh[i] - ll[i];
        v = 0.66 * ((r > 0 ? (m[i] - ll[i]) / r : 0.5) - 0.5) + 0.67 * v;
        v = Math.max(-0.999, Math.min(0.999, v));
        f = 0.5 * Math.log((1 + v) / (1 - v)) + 0.5 * f;
        fish[i] = f;
      }
      return { lines: [line("Fisher", fish), line("Trigger", shift(fish, 1), { color: COLORS.b })], levels: [0] };
    },
  },
  elderRay: {
    fullLabel: "Elder Ray",
    shortLabel: "ELDER",
    group: "momentum",
    paramDefs: [P("period", "EMA", 13)],
    compute: (c, p) => {
      const e = ema(closes(c), p.period);
      return {
        lines: [
          line("Bull power", highs(c).map((h, i) => h - e[i]), { kind: "histogram", color: COLORS.up }),
          line("Bear power", lows(c).map((l, i) => l - e[i]), { kind: "histogram", color: COLORS.down }),
        ],
        levels: [0],
      };
    },
  },
  forceIndex: {
    fullLabel: "Force Index",
    shortLabel: "FI",
    group: "volume",
    paramDefs: [P("period", "EMA", 13)],
    compute: (c, p) => ({
      lines: [line("Force", ema(c.map((x, i) => (i ? (x.close - c[i - 1].close) * vol(x) : N)), p.period))],
      levels: [0],
    }),
  },
  psychLine: {
    fullLabel: "Psychological Line",
    shortLabel: "PSY",
    group: "momentum",
    paramDefs: [P("period", "Period", 12)],
    compute: (c, p) => ({
      lines: [line("PSY", sma(c.map((x, i) => (i ? (x.close > c[i - 1].close ? 100 : 0) : N)), p.period))],
      levels: [75, 25],
    }),
  },
  qstick: {
    fullLabel: "Qstick",
    shortLabel: "QSTICK",
    group: "momentum",
    paramDefs: [P("period", "Period", 8)],
    compute: (c, p) => ({ lines: [line("Qstick", sma(c.map((x) => x.close - x.open), p.period), { kind: "histogram", colorBy: "sign" })], levels: [0] }),
  },
  rwi: {
    fullLabel: "Random Walk Index",
    shortLabel: "RWI",
    group: "trend",
    paramDefs: [P("period", "Period", 14, 2)],
    compute: (c, p) => {
      const tr = trueRange(c);
      const hiOut = new Array(c.length).fill(N);
      const loOut = new Array(c.length).fill(N);
      for (let i = p.period; i < c.length; i += 1) {
        let hi = 0;
        let lo = 0;
        let trSum = 0;
        for (let k = 1; k <= p.period; k += 1) {
          trSum += tr[i - k + 1];
          const a = (trSum / k) * Math.sqrt(k);
          if (a <= 0) continue;
          hi = Math.max(hi, (c[i].high - c[i - k].low) / a);
          lo = Math.max(lo, (c[i - k].high - c[i].low) / a);
        }
        hiOut[i] = hi;
        loOut[i] = lo;
      }
      return { lines: [line("RWI high", hiOut, { color: COLORS.up }), line("RWI low", loOut, { color: COLORS.down })], levels: [1] };
    },
  },
  rSquared: {
    fullLabel: "Regression Coefficient (R²)",
    shortLabel: "R²",
    group: "trend",
    paramDefs: [P("period", "Period", 20, 2)],
    compute: (c, p) => ({ lines: [line("R²", linreg(closes(c), p.period).r2)], levels: [0.7] }),
  },
  rvi: {
    fullLabel: "Relative Volatility Index",
    shortLabel: "RVI",
    group: "volatility",
    paramDefs: [P("stdev", "Std dev period", 10, 2), P("period", "Smoothing", 14)],
    compute: (c, p) => {
      const cl = closes(c);
      const s = stdev(cl, p.stdev);
      const u = rma(cl.map((v, i) => (i && ok(s[i]) ? (v > cl[i - 1] ? s[i] : 0) : N)), p.period);
      const d = rma(cl.map((v, i) => (i && ok(s[i]) ? (v < cl[i - 1] ? s[i] : 0) : N)), p.period);
      return { lines: [line("RVI", u.map((x, i) => (x + d[i] > 0 ? (100 * x) / (x + d[i]) : 50)))], levels: [70, 50, 30] };
    },
  },
  schaff: {
    fullLabel: "Schaff Trend Cycle",
    shortLabel: "STC",
    group: "momentum",
    paramDefs: [P("fast", "Fast", 23), P("slow", "Slow", 50), P("cycle", "Cycle", 10, 2)],
    compute: (c, p) => {
      const cl = closes(c);
      const macd = sub(ema(cl, p.fast), ema(cl, p.slow));
      const stochSmooth = (src) => {
        const hh = highest(src, p.cycle);
        const ll = lowest(src, p.cycle);
        const out = new Array(src.length).fill(N);
        let prev = N;
        for (let i = 0; i < src.length; i += 1) {
          if (!ok(hh[i])) continue;
          const k = hh[i] > ll[i] ? (100 * (src[i] - ll[i])) / (hh[i] - ll[i]) : ok(prev) ? prev : 50;
          prev = ok(prev) ? prev + 0.5 * (k - prev) : k;
          out[i] = prev;
        }
        return out;
      };
      return { lines: [line("STC", stochSmooth(stochSmooth(macd)))], levels: [75, 25] };
    },
  },
  shinohara: {
    fullLabel: "Shinohara Intensity Ratio",
    shortLabel: "SHIN",
    group: "momentum",
    paramDefs: [P("period", "Period", 26)],
    compute: (c, p) => {
      const n = p.period;
      const strongUp = rollingSum(c.map((x, i) => (i ? x.high - c[i - 1].close : N)), n);
      const strongDn = rollingSum(c.map((x, i) => (i ? c[i - 1].close - x.low : N)), n);
      const weakUp = rollingSum(c.map((x) => x.high - x.open), n);
      const weakDn = rollingSum(c.map((x) => x.open - x.low), n);
      return {
        lines: [
          line("Strong", strongUp.map((v, i) => (strongDn[i] ? (100 * v) / strongDn[i] : N))),
          line("Weak", weakUp.map((v, i) => (weakDn[i] ? (100 * v) / weakDn[i] : N)), { color: COLORS.b }),
        ],
        levels: [100],
      };
    },
  },
  trendIntensity: {
    fullLabel: "Trend Intensity Index",
    shortLabel: "TII",
    group: "trend",
    paramDefs: [P("maPeriod", "SMA period", 60, 2), P("period", "Deviation period", 30, 2), P("signal", "Signal", 9)],
    compute: (c, p) => {
      const cl = closes(c);
      const m = sma(cl, p.maPeriod);
      const dev = cl.map((v, i) => v - m[i]);
      const pos = rollingSum(dev.map((d) => (ok(d) ? Math.max(0, d) : N)), p.period);
      const neg = rollingSum(dev.map((d) => (ok(d) ? Math.max(0, -d) : N)), p.period);
      const tii = pos.map((v, i) => (v + neg[i] > 0 ? (100 * v) / (v + neg[i]) : 50));
      return { lines: [line("TII", tii), line("Signal", ema(tii, p.signal), { color: COLORS.b })], levels: [80, 50, 20] };
    },
  },
  vhf: {
    fullLabel: "Vertical Horizontal Filter",
    shortLabel: "VHF",
    group: "trend",
    paramDefs: [P("period", "Period", 28, 2)],
    compute: (c, p) => {
      const cl = closes(c);
      const hc = highest(cl, p.period);
      const lc = lowest(cl, p.period);
      const noise = rollingSum(cl.map((v, i) => (i ? Math.abs(v - cl[i - 1]) : N)), p.period);
      return { lines: [line("VHF", hc.map((h, i) => (noise[i] ? (h - lc[i]) / noise[i] : N)))] };
    },
  },
  volumeRoc: {
    fullLabel: "Volume Rate of Change",
    shortLabel: "VROC",
    group: "volume",
    paramDefs: [P("period", "Period", 14)],
    compute: (c, p) => ({ lines: [line("VROC", roc(vols(c), p.period), { kind: "histogram", colorBy: "sign" })], levels: [0] }),
  },
  vortex: {
    fullLabel: "Vortex Indicator",
    shortLabel: "VI",
    group: "trend",
    paramDefs: [P("period", "Period", 14, 2)],
    compute: (c, p) => {
      const tr = rollingSum(trueRange(c).map((v, i) => (i ? v : N)), p.period);
      const vp = rollingSum(c.map((x, i) => (i ? Math.abs(x.high - c[i - 1].low) : N)), p.period);
      const vm = rollingSum(c.map((x, i) => (i ? Math.abs(x.low - c[i - 1].high) : N)), p.period);
      return {
        lines: [line("VI+", vp.map((v, i) => v / tr[i]), { color: COLORS.up }), line("VI−", vm.map((v, i) => v / tr[i]), { color: COLORS.down })],
        levels: [1],
      };
    },
  },

  // ===== Candlestick patterns (markers on the price chart) =====
  cpAbandonedBaby: pattern("Abandoned Baby", "AB", "neutral", (c, i) => {
    if (i < 2) return false;
    const [a, b, x] = [c[i - 2], c[i - 1], c[i]];
    if (!isDoji(b)) return false;
    const bull = red(a) && green(x) && b.high < a.low && b.high < x.low;
    const bear = green(a) && red(x) && b.low > a.high && b.low > x.high;
    return bull || bear;
  }),
  cpBearEngulfing: pattern("Bearish Engulfing", "BE", "bearish", (c, i) =>
    i >= 1 && green(c[i - 1]) && red(c[i]) && c[i].open >= c[i - 1].close && c[i].close <= c[i - 1].open && body(c[i]) > body(c[i - 1])
  ),
  cpBullEngulfing: pattern("Bullish Engulfing", "BuE", "bullish", (c, i) =>
    i >= 1 && red(c[i - 1]) && green(c[i]) && c[i].open <= c[i - 1].close && c[i].close >= c[i - 1].open && body(c[i]) > body(c[i - 1])
  ),
  cpBearHammer: pattern("Bearish Hammer", "BH", "bearish", (c, i) => hammerShape(c[i]) && red(c[i]) && downTrend(c, i)),
  cpBullHammer: pattern("Bullish Hammer", "H", "bullish", (c, i) => hammerShape(c[i]) && green(c[i]) && downTrend(c, i)),
  cpBearInvHammer: pattern("Bearish Inverted Hammer", "BIH", "bearish", (c, i) => invHammerShape(c[i]) && red(c[i]) && downTrend(c, i)),
  cpBullInvHammer: pattern("Bullish Inverted Hammer", "IH", "bullish", (c, i) => invHammerShape(c[i]) && green(c[i]) && downTrend(c, i)),
  cpBearHarami: pattern("Bearish Harami", "BHa", "bearish", (c, i) =>
    i >= 1 && green(c[i - 1]) && bigBody(c, i - 1) && red(c[i]) && !isDoji(c[i]) && c[i].open <= c[i - 1].close && c[i].close >= c[i - 1].open
  ),
  cpBullHarami: pattern("Bullish Harami", "Ha", "bullish", (c, i) =>
    i >= 1 && red(c[i - 1]) && bigBody(c, i - 1) && green(c[i]) && !isDoji(c[i]) && c[i].open >= c[i - 1].close && c[i].close <= c[i - 1].open
  ),
  cpBearHaramiCross: pattern("Bearish Harami Cross", "BHX", "bearish", (c, i) =>
    i >= 1 && green(c[i - 1]) && bigBody(c, i - 1) && isDoji(c[i]) && Math.max(c[i].open, c[i].close) <= c[i - 1].close && Math.min(c[i].open, c[i].close) >= c[i - 1].open
  ),
  cpBullHaramiCross: pattern("Bullish Harami Cross", "HX", "bullish", (c, i) =>
    i >= 1 && red(c[i - 1]) && bigBody(c, i - 1) && isDoji(c[i]) && Math.max(c[i].open, c[i].close) <= c[i - 1].open && Math.min(c[i].open, c[i].close) >= c[i - 1].close
  ),
  cpBearMarubozu: pattern("Bearish Marubozu", "BM", "bearish", (c, i) => red(c[i]) && body(c[i]) >= 0.95 * range(c[i]) && bigBody(c, i)),
  cpBullMarubozu: pattern("Bullish Marubozu", "M", "bullish", (c, i) => green(c[i]) && body(c[i]) >= 0.95 * range(c[i]) && bigBody(c, i)),
  cpBearSpinningTop: pattern("Bearish Spinning Top", "BST", "bearish", (c, i) => {
    const x = c[i];
    const b = body(x);
    return red(x) && b > 0.1 * range(x) && b <= 0.3 * range(x) && upperWick(x) >= b && lowerWick(x) >= b;
  }),
  cpBullSpinningTop: pattern("Bullish Spinning Top", "ST", "bullish", (c, i) => {
    const x = c[i];
    const b = body(x);
    return green(x) && b > 0.1 * range(x) && b <= 0.3 * range(x) && upperWick(x) >= b && lowerWick(x) >= b;
  }),
  cpDarkCloud: pattern("Dark Cloud Cover", "DC", "bearish", (c, i) =>
    i >= 1 && green(c[i - 1]) && bigBody(c, i - 1) && red(c[i]) && c[i].open >= c[i - 1].close && c[i].close < mid(c[i - 1]) && c[i].close > c[i - 1].open
  ),
  cpPiercing: pattern("Piercing Line", "PL", "bullish", (c, i) =>
    i >= 1 && red(c[i - 1]) && bigBody(c, i - 1) && green(c[i]) && c[i].open <= c[i - 1].close && c[i].close > mid(c[i - 1]) && c[i].close < c[i - 1].open
  ),
  cpDoji: pattern("Doji", "D", "neutral", (c, i) => isDoji(c[i])),
  cpDragonflyDoji: pattern("Dragonfly Doji", "DD", "bullish", (c, i) => isDoji(c[i]) && upperWick(c[i]) <= 0.1 * range(c[i]) && lowerWick(c[i]) >= 0.6 * range(c[i])),
  cpGravestoneDoji: pattern("Gravestone Doji", "GD", "bearish", (c, i) => isDoji(c[i]) && lowerWick(c[i]) <= 0.1 * range(c[i]) && upperWick(c[i]) >= 0.6 * range(c[i])),
  cpDownsideTasuki: pattern("Downside Tasuki Gap", "DTG", "bearish", (c, i) => {
    if (i < 2) return false;
    const [a, b, x] = [c[i - 2], c[i - 1], c[i]];
    return red(a) && red(b) && b.high < a.low && green(x) && x.open < b.open && x.open > b.close && x.close > b.high && x.close < a.low;
  }),
  cpEveningDojiStar: pattern("Evening Doji Star", "EDS", "bearish", starTest(false, true)),
  cpEveningStar: pattern("Evening Star", "ES", "bearish", starTest(false, false)),
  cpMorningDojiStar: pattern("Morning Doji Star", "MDS", "bullish", starTest(true, true)),
  cpMorningStar: pattern("Morning Star", "MS", "bullish", starTest(true, false)),
  cpHangingMan: pattern("Hanging Man", "HM", "bearish", (c, i) => hammerShape(c[i]) && upTrend(c, i)),
  cpShootingStar: pattern("Shooting Star", "SS", "bearish", (c, i) => invHammerShape(c[i]) && upTrend(c, i)),
  cpThreeBlackCrows: pattern("Three Black Crows", "3BC", "bearish", (c, i) => {
    if (i < 2) return false;
    const [a, b, x] = [c[i - 2], c[i - 1], c[i]];
    return [a, b, x].every((k) => red(k) && body(k) >= 0.5 * range(k)) &&
      b.close < a.close && x.close < b.close && b.open <= a.open && b.open >= a.close && x.open <= b.open && x.open >= b.close;
  }),
  cpThreeWhiteSoldiers: pattern("Three White Soldiers", "3WS", "bullish", (c, i) => {
    if (i < 2) return false;
    const [a, b, x] = [c[i - 2], c[i - 1], c[i]];
    return [a, b, x].every((k) => green(k) && body(k) >= 0.5 * range(k)) &&
      b.close > a.close && x.close > b.close && b.open >= a.open && b.open <= a.close && x.open >= b.open && x.open <= b.close;
  }),
  cpTweezerBottom: pattern("Tweezer Bottom", "TB", "bullish", (c, i) =>
    i >= 1 && downTrend(c, i - 1) && red(c[i - 1]) && green(c[i]) && Math.abs(c[i].low - c[i - 1].low) <= 0.05 * Math.max(range(c[i]), range(c[i - 1]))
  ),
  cpTweezerTop: pattern("Tweezer Top", "TT", "bearish", (c, i) =>
    i >= 1 && upTrend(c, i - 1) && green(c[i - 1]) && red(c[i]) && Math.abs(c[i].high - c[i - 1].high) <= 0.05 * Math.max(range(c[i]), range(c[i - 1]))
  ),
};

/** Wilder's Swing Index; the "limit move" is taken as limitAtr × ATR(14) since FX/crypto have none. */
function swingIndex(c, limitAtr) {
  const a = atr(c, 14);
  return c.map((x, i) => {
    if (!i || !ok(a[i])) return N;
    const pc = c[i - 1].close;
    const po = c[i - 1].open;
    const hc = Math.abs(x.high - pc);
    const lc = Math.abs(x.low - pc);
    const hl = x.high - x.low;
    const cpo = Math.abs(pc - po);
    let r;
    if (hc >= lc && hc >= hl) r = hc - 0.5 * lc + 0.25 * cpo;
    else if (lc >= hc && lc >= hl) r = lc - 0.5 * hc + 0.25 * cpo;
    else r = hl + 0.25 * cpo;
    const t = limitAtr * a[i];
    if (!(r > 0) || !(t > 0)) return 0;
    const k = Math.max(hc, lc);
    return (50 * ((x.close - pc + 0.5 * (x.close - x.open) + 0.25 * (pc - po)) / r) * k) / t;
  });
}

export const GO_INDICATOR_KEYS = Object.keys(GO_INDICATORS);

/** Default color for an indicator's first line (later lines use their own colors). */
export const GO_DEFAULT_COLORS = ["#38bdf8", "#f59e0b", "#a78bfa", "#f472b6", "#22d3ee", "#eab308", "#84cc16"];

/** Converts an aligned value array to lightweight-charts points (NaN → whitespace gap). */
export function toSeriesPoints(candles, values, opts = {}) {
  const out = [];
  let started = false;
  for (let i = 0; i < candles.length; i += 1) {
    const v = values[i];
    if (Number.isFinite(v)) {
      started = true;
      const pt = { time: candles[i].time, value: v };
      if (opts.colorBy === "sign") pt.color = v >= 0 ? opts.upColor : opts.downColor;
      else if (opts.colorBy === "candle") pt.color = candles[i].close >= candles[i].open ? opts.upColor : opts.downColor;
      else if (opts.colorBy === "rising" || opts.colorBy === "falling") {
        const prev = values[i - 1];
        const growing = Number.isFinite(prev) ? Math.abs(v) >= Math.abs(prev) : true;
        pt.color = growing ? opts.upColor : opts.downColor;
      }
      out.push(pt);
    } else if (started) {
      out.push({ time: candles[i].time });
    }
  }
  return out;
}
